// ============================================================================
// AcademyStudio.jsx — Passo 44 — LMS v9.0
// Estúdio Acadêmico com Drag-and-Drop via @dnd-kit (Art. 103.2 + 105.1)
// PROIBIDO: react-beautiful-dnd
// MC1: infra + mock data
// MC2: SortableLessonItem com GripVertical handle
// MC3: DndContext + SortableContext + arrayMove
// ============================================================================
import React, { useCallback, useMemo, useState } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  BookOpen,
  ChevronDown,
  ChevronRight,
  Clock,
  Edit3,
  GraduationCap,
  GripVertical,
  MonitorPlay,
  Plus,
  Save,
  Trash2,
  Video,
  FileText,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff,
} from 'lucide-react';

// ── Tipos de aula ─────────────────────────────────────────────────────────────
const TIPOS_DE_AULA = {
  VIDEO:   { label: 'Vídeo',   icon: Video,     color: 'text-blue-500' },
  TEXT:    { label: 'Texto',   icon: FileText,  color: 'text-emerald-500' },
  QUIZ:    { label: 'Quiz',    icon: CheckCircle, color: 'text-amber-500' },
};

// ── MC1: Mock Data de Alta Fidelidade (Academia Jurídica RDA) ─────────────────
const INITIAL_MODULES = [
  {
    id: 'mod-1',
    title: 'Ética e Deontologia da OAB',
    description: 'Fundamentos do exercício ético da advocacia e o Estatuto da OAB.',
    published: true,
    lessons: [
      { id: 'les-1-1', title: 'Introdução ao Estatuto da Advocacia', type: 'VIDEO',  duration: 18, published: true  },
      { id: 'les-1-2', title: 'Sigilo Profissional e seus Limites',  type: 'VIDEO',  duration: 22, published: true  },
      { id: 'les-1-3', title: 'Conflito de Interesses e Incompatibilidades', type: 'TEXT', duration: 12, published: true },
      { id: 'les-1-4', title: 'Avaliação: Ética OAB',               type: 'QUIZ',   duration: 15, published: false },
    ],
  },
  {
    id: 'mod-2',
    title: 'Processo Civil (CPC 2015)',
    description: 'Estudo sistemático do novo Código de Processo Civil e suas aplicações práticas.',
    published: true,
    lessons: [
      { id: 'les-2-1', title: 'Normas Fundamentais do CPC',          type: 'VIDEO',  duration: 25, published: true  },
      { id: 'les-2-2', title: 'Competência Absoluta e Relativa',     type: 'TEXT',   duration: 20, published: true  },
      { id: 'les-2-3', title: 'Petição Inicial: Requisitos Formais', type: 'VIDEO',  duration: 30, published: true  },
      { id: 'les-2-4', title: 'Tutelas Provisórias de Urgência',     type: 'VIDEO',  duration: 35, published: false },
      { id: 'les-2-5', title: 'Avaliação: CPC Módulo 2',             type: 'QUIZ',   duration: 20, published: false },
    ],
  },
  {
    id: 'mod-3',
    title: 'Direito do Trabalho e CLT',
    description: 'Reforma Trabalhista, contratos atípicos e o contencioso do trabalho.',
    published: true,
    lessons: [
      { id: 'les-3-1', title: 'Contrato de Trabalho: Modalidades',   type: 'VIDEO',  duration: 28, published: true  },
      { id: 'les-3-2', title: 'FGTS, Férias e 13º Salário',          type: 'TEXT',   duration: 15, published: true  },
      { id: 'les-3-3', title: 'Reclamação Trabalhista no JUS Virtual', type: 'VIDEO', duration: 40, published: true },
    ],
  },
  {
    id: 'mod-4',
    title: 'Contratos Empresariais',
    description: 'Elaboração e revisão de contratos societários, NDA e licenciamento.',
    published: false,
    lessons: [
      { id: 'les-4-1', title: 'Elementos Essenciais do Contrato',    type: 'VIDEO',  duration: 20, published: false },
      { id: 'les-4-2', title: 'NDA e Acordos de Confidencialidade',  type: 'TEXT',   duration: 18, published: false },
    ],
  },
  {
    id: 'mod-5',
    title: 'Tributário Essencial para Advogados',
    description: 'ISS, IRPJ, contribuições previdenciárias e planejamento tributário.',
    published: false,
    lessons: [
      { id: 'les-5-1', title: 'Tributos e Obrigações do Escritório', type: 'VIDEO',  duration: 22, published: false },
      { id: 'les-5-2', title: 'Simples Nacional para OAB',           type: 'TEXT',   duration: 14, published: false },
    ],
  },
];

// ── MC2: SortableLessonItem (Art. 105.1 — handle GripVertical) ────────────────
function SortableLessonItem({ lesson, moduleId, index, onTogglePublished, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: lesson.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex:  isDragging ? 100 : 'auto',
  };

  const LessonIcon = TIPOS_DE_AULA[lesson.type]?.icon || FileText;
  const lessonColor = TIPOS_DE_AULA[lesson.type]?.color || 'text-gray-400';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex items-center gap-2 bg-white border rounded-xl px-3 py-2.5 shadow-sm transition-all ${
        isDragging
          ? 'border-[#C5A059] shadow-lg ring-2 ring-[#C5A059]/30 bg-amber-50'
          : 'border-gray-200 hover:border-gray-300 hover:shadow'
      }`}
    >
      {/* Art. 105.1: GripVertical como handle exclusivo do drag */}
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="flex-shrink-0 cursor-grab active:cursor-grabbing p-1 rounded text-gray-300 hover:text-gray-500 hover:bg-gray-100 transition touch-none"
        title="Arrastar para reordenar"
        aria-label="Arrastar aula"
      >
        <GripVertical className="w-4 h-4" />
      </button>

      {/* Índice */}
      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-100 text-gray-500 text-[10px] font-extrabold flex items-center justify-center">
        {index + 1}
      </span>

      {/* Ícone de tipo */}
      <LessonIcon className={`flex-shrink-0 w-3.5 h-3.5 ${lessonColor}`} />

      {/* Título */}
      <span className={`flex-1 text-xs font-medium truncate ${lesson.published ? 'text-gray-800' : 'text-gray-400'}`}>
        {lesson.title}
      </span>

      {/* Duração */}
      <span className="flex-shrink-0 flex items-center gap-1 text-[10px] text-gray-400">
        <Clock className="w-3 h-3" />
        {lesson.duration}min
      </span>

      {/* Status */}
      <span className={`flex-shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${
        lesson.published
          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
          : 'bg-gray-50 text-gray-400 border-gray-200'
      }`}>
        {lesson.published ? 'PUB' : 'RASCUNHO'}
      </span>

      {/* Ações (visíveis no hover) */}
      <div className="flex-shrink-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          type="button"
          onClick={() => onTogglePublished(moduleId, lesson.id)}
          className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition"
          title={lesson.published ? 'Ocultar' : 'Publicar'}
        >
          {lesson.published ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
        </button>
        <button
          type="button"
          onClick={() => onEdit(moduleId, lesson)}
          className="p-1 rounded hover:bg-blue-50 text-gray-400 hover:text-blue-600 transition"
          title="Editar"
        >
          <Edit3 className="w-3 h-3" />
        </button>
        <button
          type="button"
          onClick={() => onDelete(moduleId, lesson.id)}
          className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 transition"
          title="Remover"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

// ── Accordion de Módulo ───────────────────────────────────────────────────────
function ModuleAccordion({ module, expanded, onToggleExpand, onDragEnd, onToggleLessonPublished, onEditLesson, onDeleteLesson, onToggleModulePublished, onAddLesson }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const lessonIds = useMemo(() => module.lessons.map((l) => l.id), [module.lessons]);
  const totalMins = module.lessons.reduce((a, l) => a + (l.duration || 0), 0);

  return (
    <div className={`border rounded-2xl overflow-hidden transition-all ${
      module.published ? 'border-gray-200' : 'border-dashed border-gray-300 opacity-80'
    }`}>
      {/* Cabeçalho do módulo */}
      <div
        className="flex items-center gap-3 px-4 py-3 bg-white cursor-pointer hover:bg-gray-50 select-none"
        onClick={onToggleExpand}
      >
        <div className="flex-shrink-0 w-8 h-8 rounded-xl bg-[#20243B] flex items-center justify-center">
          <BookOpen className="w-4 h-4 text-[#C5A059]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-extrabold text-[#20243B] truncate">{module.title}</span>
            <span className={`flex-shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${
              module.published
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : 'bg-amber-50 text-amber-600 border-amber-200'
            }`}>
              {module.published ? 'PUBLICADO' : 'RASCUNHO'}
            </span>
          </div>
          <div className="flex items-center gap-3 mt-0.5">
            <span className="text-[10px] text-gray-400">{module.lessons.length} aula{module.lessons.length !== 1 ? 's' : ''}</span>
            <span className="text-[10px] text-gray-300">·</span>
            <span className="text-[10px] text-gray-400 flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" /> {totalMins}min</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={() => onToggleModulePublished(module.id)}
            className="text-[10px] font-bold px-2 py-1 rounded-lg border border-gray-200 hover:bg-gray-100 text-gray-500 transition"
            title={module.published ? 'Despublicar módulo' : 'Publicar módulo'}
          >
            {module.published ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
          </button>
          <button
            type="button"
            onClick={() => onAddLesson(module.id)}
            className="text-[10px] font-bold px-2 py-1 rounded-lg bg-[#20243B] text-[#C5A059] hover:bg-[#2d3352] transition flex items-center gap-1"
          >
            <Plus className="w-3 h-3" /> Aula
          </button>
        </div>
        <div className="flex-shrink-0 text-gray-400">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </div>
      </div>

      {/* Lista de aulas (sortable) */}
      {expanded && (
        <div className="bg-gray-50 border-t border-gray-100 px-4 py-3 space-y-1.5">
          {module.lessons.length === 0 ? (
            <div className="text-center py-6 text-xs text-gray-400">
              Nenhuma aula neste módulo.
              <button type="button" onClick={() => onAddLesson(module.id)} className="ml-2 text-[#C5A059] font-bold hover:underline">+ Adicionar</button>
            </div>
          ) : (
            // MC3: DndContext + SortableContext + handleDragEnd
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={(event) => onDragEnd(module.id, event)}
            >
              <SortableContext items={lessonIds} strategy={verticalListSortingStrategy}>
                {module.lessons.map((lesson, idx) => (
                  <SortableLessonItem
                    key={lesson.id}
                    lesson={lesson}
                    moduleId={module.id}
                    index={idx}
                    onTogglePublished={onToggleLessonPublished}
                    onEdit={onEditLesson}
                    onDelete={onDeleteLesson}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </div>
      )}
    </div>
  );
}

// ── Formulário de Nova Aula ───────────────────────────────────────────────────
const EMPTY_LESSON_FORM = { title: '', type: 'VIDEO', duration: 15, published: false };

function LessonFormModal({ moduleTitle, initial, onSave, onClose }) {
  const [form, setForm] = useState(initial || EMPTY_LESSON_FORM);
  return (
    <div className="fixed inset-0 z-[500] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-sm mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="bg-[#20243B] px-5 py-4">
          <div className="text-[11px] font-black uppercase tracking-widest text-[#C5A059]">
            {initial ? 'Editar Aula' : 'Nova Aula'}
          </div>
          <div className="text-[10px] text-white/60 mt-0.5 truncate">Módulo: {moduleTitle}</div>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div>
            <label className="text-[10px] font-bold text-gray-500 block mb-1">Título *</label>
            <input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} className="w-full text-xs border border-gray-300 rounded-lg px-3 py-2 outline-none focus:border-[#C5A059]" placeholder="Ex: Introdução ao CPC" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-bold text-gray-500 block mb-1">Tipo</label>
              <select value={form.type} onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))} className="w-full text-xs border border-gray-300 rounded-lg px-3 py-2 outline-none focus:border-[#C5A059]">
                {Object.entries(TIPOS_DE_AULA).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold text-gray-500 block mb-1">Duração (min)</label>
              <input type="number" min={1} max={180} value={form.duration} onChange={(e) => setForm((p) => ({ ...p, duration: Number(e.target.value) }))} className="w-full text-xs border border-gray-300 rounded-lg px-3 py-2 outline-none focus:border-[#C5A059]" />
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm((p) => ({ ...p, published: e.target.checked }))} className="accent-emerald-500" />
            <span className="text-xs text-gray-700 font-medium">Publicar imediatamente</span>
          </label>
        </div>
        <div className="px-5 pb-5 flex gap-2">
          <button type="button" onClick={onClose} className="flex-1 py-2 rounded-lg border border-gray-300 text-xs font-bold text-gray-600 hover:bg-gray-50 transition">Cancelar</button>
          <button type="button" disabled={!form.title.trim()} onClick={() => onSave(form)} className="flex-1 py-2 rounded-lg bg-emerald-600 text-white text-xs font-extrabold hover:bg-emerald-700 disabled:opacity-50 transition">
            <Save className="w-3 h-3 inline mr-1" />{initial ? 'Salvar' : 'Criar Aula'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Componente Principal ──────────────────────────────────────────────────────
export function AcademyStudio() {
  const [modules, setModules] = useState(INITIAL_MODULES);
  const [expanded, setExpanded] = useState({ 'mod-1': true });
  const [lessonModal, setLessonModal] = useState(null); // { moduleId, lesson? }
  const [newModuleTitle, setNewModuleTitle] = useState('');
  const [showModuleForm, setShowModuleForm] = useState(false);

  // ── MC3: handleDragEnd por módulo ──────────────────────────────────────────
  const handleDragEnd = useCallback((moduleId, event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setModules((mods) =>
      mods.map((mod) => {
        if (mod.id !== moduleId) return mod;
        const oldIdx = mod.lessons.findIndex((l) => l.id === active.id);
        const newIdx = mod.lessons.findIndex((l) => l.id === over.id);
        if (oldIdx === -1 || newIdx === -1) return mod;
        return { ...mod, lessons: arrayMove(mod.lessons, oldIdx, newIdx) };
      })
    );
  }, []);

  const handleToggleExpand = useCallback((moduleId) => {
    setExpanded((prev) => ({ ...prev, [moduleId]: !prev[moduleId] }));
  }, []);

  const handleToggleLessonPublished = useCallback((moduleId, lessonId) => {
    setModules((mods) =>
      mods.map((mod) =>
        mod.id !== moduleId ? mod : {
          ...mod,
          lessons: mod.lessons.map((l) => l.id === lessonId ? { ...l, published: !l.published } : l),
        }
      )
    );
  }, []);

  const handleToggleModulePublished = useCallback((moduleId) => {
    setModules((mods) =>
      mods.map((mod) => mod.id === moduleId ? { ...mod, published: !mod.published } : mod)
    );
  }, []);

  const handleEditLesson = useCallback((moduleId, lesson) => {
    setLessonModal({ moduleId, lesson });
  }, []);

  const handleDeleteLesson = useCallback((moduleId, lessonId) => {
    if (!window.confirm('Remover esta aula?')) return;
    setModules((mods) =>
      mods.map((mod) =>
        mod.id !== moduleId ? mod : { ...mod, lessons: mod.lessons.filter((l) => l.id !== lessonId) }
      )
    );
  }, []);

  const handleAddLesson = useCallback((moduleId) => {
    setLessonModal({ moduleId, lesson: null });
  }, []);

  const handleSaveLesson = useCallback((form) => {
    if (!lessonModal) return;
    const { moduleId, lesson } = lessonModal;
    setModules((mods) =>
      mods.map((mod) => {
        if (mod.id !== moduleId) return mod;
        if (lesson) {
          return { ...mod, lessons: mod.lessons.map((l) => l.id === lesson.id ? { ...l, ...form } : l) };
        }
        const newLesson = { id: `les-${Date.now()}`, ...form };
        return { ...mod, lessons: [...mod.lessons, newLesson] };
      })
    );
    setLessonModal(null);
  }, [lessonModal]);

  const handleAddModule = useCallback(() => {
    if (!newModuleTitle.trim()) return;
    const newMod = {
      id: `mod-${Date.now()}`,
      title: newModuleTitle.trim(),
      description: '',
      published: false,
      lessons: [],
    };
    setModules((prev) => [...prev, newMod]);
    setExpanded((prev) => ({ ...prev, [newMod.id]: true }));
    setNewModuleTitle('');
    setShowModuleForm(false);
  }, [newModuleTitle]);

  // ── Stats ──────────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const totalLessons  = modules.reduce((a, m) => a + m.lessons.length, 0);
    const pubLessons    = modules.reduce((a, m) => a + m.lessons.filter((l) => l.published).length, 0);
    const totalMins     = modules.reduce((a, m) => a + m.lessons.reduce((b, l) => b + (l.duration || 0), 0), 0);
    return { totalLessons, pubLessons, totalMins, pubModules: modules.filter((m) => m.published).length };
  }, [modules]);

  const modalModule = lessonModal ? modules.find((m) => m.id === lessonModal.moduleId) : null;

  return (
    <div className="space-y-5 pb-10">

      {/* ── Cabeçalho ── */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-sm font-extrabold text-[#20243B] uppercase tracking-widest flex items-center gap-2">
            <MonitorPlay className="w-4 h-4 text-[#C5A059]" /> Estúdio — LMS v9.0
          </h2>
          <p className="text-[10px] text-gray-400 mt-0.5">Art. 103.2 + 105.1 · @dnd-kit · Drag-and-Drop para reordenação de aulas</p>
        </div>
        <button
          type="button"
          onClick={() => setShowModuleForm((v) => !v)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#20243B] text-[#C5A059] text-xs font-extrabold uppercase tracking-widest hover:bg-[#2d3352] transition"
        >
          <Plus className="w-3.5 h-3.5" /> Novo Módulo
        </button>
      </div>

      {/* ── KPI cards ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-[#20243B] rounded-xl px-4 py-3 text-white">
          <div className="text-[10px] font-extrabold uppercase tracking-widest text-[#C5A059]">Módulos</div>
          <div className="text-2xl font-black mt-1">{modules.length}</div>
          <div className="text-[10px] text-white/50">{stats.pubModules} publicados</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl px-4 py-3">
          <div className="text-[10px] font-extrabold uppercase tracking-widest text-gray-400">Aulas</div>
          <div className="text-2xl font-black text-gray-800 mt-1">{stats.totalLessons}</div>
          <div className="text-[10px] text-gray-400">{stats.pubLessons} publicadas</div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
          <div className="text-[10px] font-extrabold uppercase tracking-widest text-emerald-700">Carga Horária</div>
          <div className="text-2xl font-black text-emerald-800 mt-1">{Math.floor(stats.totalMins / 60)}h{String(stats.totalMins % 60).padStart(2, '0')}</div>
          <div className="text-[10px] text-emerald-600">{stats.totalMins} minutos</div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
          <div className="text-[10px] font-extrabold uppercase tracking-widest text-blue-700">Publicação</div>
          <div className="text-2xl font-black text-blue-800 mt-1">
            {stats.totalLessons ? Math.round(stats.pubLessons / stats.totalLessons * 100) : 0}%
          </div>
          <div className="text-[10px] text-blue-600">aulas publicadas</div>
        </div>
      </div>

      {/* ── Formulário de novo módulo ── */}
      {showModuleForm && (
        <div className="bg-white border border-[#C5A059]/30 rounded-2xl px-4 py-3 flex items-center gap-3 shadow-sm">
          <GraduationCap className="w-5 h-5 text-[#C5A059] flex-shrink-0" />
          <input
            autoFocus
            value={newModuleTitle}
            onChange={(e) => setNewModuleTitle(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAddModule(); if (e.key === 'Escape') setShowModuleForm(false); }}
            placeholder="Título do novo módulo..."
            className="flex-1 text-sm border-none outline-none text-gray-800 bg-transparent"
          />
          <button type="button" onClick={handleAddModule} disabled={!newModuleTitle.trim()} className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-extrabold hover:bg-emerald-700 disabled:opacity-50 transition">
            Criar
          </button>
          <button type="button" onClick={() => setShowModuleForm(false)} className="text-gray-400 hover:text-gray-600">
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* ── Instrução DnD ── */}
      <div className="flex items-center gap-2 text-[10px] text-gray-400 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
        <GripVertical className="w-3.5 h-3.5 text-[#C5A059]" />
        <span>Arraste pelo ícone <strong className="text-gray-600">⠿</strong> para reordenar as aulas dentro de cada módulo</span>
      </div>

      {/* ── Lista de módulos ── */}
      <div className="space-y-3">
        {modules.map((module) => (
          <ModuleAccordion
            key={module.id}
            module={module}
            expanded={!!expanded[module.id]}
            onToggleExpand={() => handleToggleExpand(module.id)}
            onDragEnd={handleDragEnd}
            onToggleLessonPublished={handleToggleLessonPublished}
            onEditLesson={handleEditLesson}
            onDeleteLesson={handleDeleteLesson}
            onToggleModulePublished={handleToggleModulePublished}
            onAddLesson={handleAddLesson}
          />
        ))}
      </div>

      {/* ── Modal de Aula ── */}
      {lessonModal && modalModule && (
        <LessonFormModal
          moduleTitle={modalModule.title}
          initial={lessonModal.lesson || null}
          onSave={handleSaveLesson}
          onClose={() => setLessonModal(null)}
        />
      )}
    </div>
  );
}

export default AcademyStudio;
